var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { encode } from 'gpt-tokenizer';
import compact from 'lodash/compact';
import intersection from 'lodash/intersection';
import objecthash from 'object-hash';
import { OpenAPIV2 } from 'openapi-types';
import { stripHtml } from 'string-strip-html';
import Zod from 'zod';
import { objectivePrefix } from '../agent/index';
import { $deref } from '../utils/openapi';
import { getDefaultContentType } from '../utils/openapi/content-type';
import { dereferencePath } from '../utils/openapi/dereference-path';
import { t } from '../utils/template';
export default class ExternalResourceDirectory {
    constructor(parser, embeddingFunctions) {
        this.embed = (api) => __awaiter(this, void 0, void 0, function* () {
            this.logger.log('Preparing OpenAPI specs for embedding');
            // <keywords>: [<api1>, <api2>]
            const pathMapping = new Map();
            // Iterate over all paths in the API
            for (const path in api.paths) {
                // Iterate over all HTTP methods for the current path
                for (const httpMethod of intersection(Object.values(OpenAPIV2.HttpMethods), Object.keys(api.paths[path]))) {
                    const fullPath = [api.info.title, path, httpMethod].join('#');
                    const operationObject = $deref(yield dereferencePath(api, httpMethod, path));
                    // Add parameters to keyPathMap
                    addParametersToPathMapping(operationObject, pathMapping, fullPath);
                    // Add request body properties to keyPathMap
                    addRequestBodyPropertiesToPathMapping(operationObject, pathMapping, fullPath);
                    // Add operation object to keyPathMap
                    addOperationObjectToPathMapping(operationObject, pathMapping, fullPath);
                }
            }
            this.logger.log('Embedding OpenAPI specs... (This might take a while for Pinecone)');
            // Process keys in batches
            yield this.processKeysInBatches(pathMapping, api);
            this.logger.log('All done with embedding, completed without errors.');
        });
        this.search = (objective, providers) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            this.logger.info('Search called with objective:', objective);
            const embedding = yield this.embeddingFunctions.createEmbeddings([
                objective,
            ]);
            const matches = yield this.embeddingFunctions.searchItems(objective, embedding[0][1], 10, providers.length > 1
                ? {
                    provider: {
                        $in: providers,
                    },
                }
                : {
                    provider: {
                        $eq: providers[0],
                    },
                });
            const pathScores = new Map();
            for (const match of matches) {
                const matchPaths = (_a = match.metadata) === null || _a === void 0 ? void 0 : _a.paths;
                for (const path of matchPaths) {
                    const score = (_b = pathScores.get(path)) !== null && _b !== void 0 ? _b : 0;
                    pathScores.set(path, score + (1 - ((_c = match.distance) !== null && _c !== void 0 ? _c : 0)));
                }
            }
            const sortedPaths = Array.from(pathScores.entries()).sort((a, b) => b[1] - a[1]);
            return sortedPaths
                .map((entry) => entry[0])
                .splice(0, 15);
        });
        this.shortlist = (objective, context, openapis) => __awaiter(this, void 0, void 0, function* () {
            var _d, _e, _f, _g, _h;
            const providers = openapis.map((openapi) => openapi.info.title);
            const matches = yield this.search(objective, providers);
            const matchDetails = [];
            for (const match of matches) {
                const [provider, path, method] = match.split('#');
                const openapi = openapis.find((o) => o.info.title === provider);
                const operationObject = (_e = (_d = openapi === null || openapi === void 0 ? void 0 : openapi.paths) === null || _d === void 0 ? void 0 : _d[path]) === null || _e === void 0 ? void 0 : _e[method];
                if (!operationObject)
                    continue;
                matchDetails.push({
                    provider,
                    method,
                    path,
                    description: stripHtml((_h = (_g = (_f = operationObject.description) !== null && _f !== void 0 ? _f : operationObject.summary) !== null && _g !== void 0 ? _g : operationObject.operationId) !== null && _h !== void 0 ? _h : match).result,
                });
            }
            const matchesStr = matchDetails.map(({ provider, method, path, description }) => {
                return `${provider}: ${method.toUpperCase()} ${path}\n'${description}'`;
            });
            const message = t([
                objectivePrefix({ objective, context }),
                'I want to figure out what external resources (APIs) need to be called to achieve this objective.',
                'Your task is to shortlist APIs for me, here is the list:',
                '{{#each matchesStr}}',
                '{{@index}}. {{this}}',
                '{{/each}}',
                'Rules:',
                '1. It is important that you take note of the index.',
                '2. You must choose at lest one API.',
                '3. You can choose multiple APIs.',
                '4. Keep the list liberal.',
                '5. The order of the list matters, start with the best fit.',
            ], {
                matchesStr,
            });
            this.logger.log('Asking LLMs to shortlist the correct APIs');
            const { indexes } = yield this.parser.llm.generateStructured({
                messages: [
                    {
                        content: message,
                        role: 'user',
                    },
                ],
                generatorName: 'api_shortlist',
                generatorDescription: 'Shortlist APIs that might work out for the objective.',
                generatorOutputSchema: Zod.object({
                    indexes: Zod.array(Zod.number()
                        .min(0)
                        .max(matchesStr.length - 1)
                        .describe('The index of API in the given list')),
                }),
            });
            return matchDetails.filter((_, index) => {
                return indexes.includes(index);
            });
        });
        this.embeddingFunctions = embeddingFunctions;
        this.parser = parser;
        this.logger = parser.logger;
    }
    processKeysInBatches(pathMapping, api) {
        return __awaiter(this, void 0, void 0, function* () {
            const allKeys = Array.from(pathMapping.keys());
            const batchSize = 1000;
            // Process keys in batches
            for (let i = 0; i < allKeys.length; i += batchSize) {
                const batchKeys = allKeys.slice(i, i + batchSize).filter((key) => !!key);
                // Create a map of trimmed keys to original keys
                const trimmedToOriginalKeyMap = Object.fromEntries(batchKeys.map((originalKey) => {
                    let trimmedKey = stripHtml(originalKey).result;
                    while (encode(trimmedKey).length > 8000) {
                        trimmedKey = trimmedKey.slice(0, -100);
                    }
                    // eslint-disable-next-line no-control-regex
                    trimmedKey = trimmedKey.replace(/[^\x00-\x7F]/g, '');
                    return [trimmedKey, originalKey];
                }));
                // Create metadata map and metadata hash map
                const metadataMap = createMetadataMap(trimmedToOriginalKeyMap, pathMapping, api);
                const metadataHashMap = createMetadataHashMap(metadataMap);
                // Retrieve stored embeddings
                const storedEmbeddings = yield this.embeddingFunctions.retrieveItems(Object.keys(trimmedToOriginalKeyMap));
                // Determine which keys need to be embedded
                const keysToEmbed = getKeysToEmbed(trimmedToOriginalKeyMap, storedEmbeddings, metadataHashMap);
                const embeddingsToStore = [];
                this.logger.log('Embedding', keysToEmbed.length, 'keys');
                const embeddingsResponse = keysToEmbed.length
                    ? yield this.embeddingFunctions.createEmbeddings(keysToEmbed)
                    : [];
                const embeddingsMap = Object.fromEntries(embeddingsResponse);
                for (const trimmedKey of Object.keys(trimmedToOriginalKeyMap)) {
                    if (keysToEmbed.includes(trimmedKey)) {
                        embeddingsToStore.push({
                            id: trimmedKey,
                            embeddings: embeddingsMap[trimmedKey],
                            metadataHash: metadataHashMap[trimmedKey],
                            metadata: metadataMap[trimmedKey],
                        });
                    }
                }
                this.logger.log('Storing', embeddingsToStore.length, 'embeddings to store');
                yield this.embeddingFunctions.upsertItems(embeddingsToStore);
            }
        });
    }
}
function addParametersToPathMapping(operationObject, keyPathMap, fullPath) {
    var _a, _b;
    const parameters = (_b = (_a = operationObject.parameters) === null || _a === void 0 ? void 0 : _a.map((p) => $deref(p))) !== null && _b !== void 0 ? _b : [];
    for (const parameter of parameters) {
        const key = compact([parameter.name, parameter.description]).join(': ');
        addKeyToPathMapping(key, keyPathMap, fullPath);
    }
}
function addRequestBodyPropertiesToPathMapping(operationObject, pathMapping, fullPath) {
    var _a, _b, _c, _d;
    const requestBody = 'requestBody' in operationObject
        ? $deref(operationObject.requestBody)
        : undefined;
    const defaultContentType = getDefaultContentType(Object.keys((_a = requestBody === null || requestBody === void 0 ? void 0 : requestBody.content) !== null && _a !== void 0 ? _a : []));
    const requestBodySchema = $deref((_c = (_b = requestBody === null || requestBody === void 0 ? void 0 : requestBody.content) === null || _b === void 0 ? void 0 : _b[defaultContentType]) === null || _c === void 0 ? void 0 : _c.schema);
    if (requestBodySchema) {
        let properties;
        if (requestBodySchema === null || requestBodySchema === void 0 ? void 0 : requestBodySchema.properties) {
            properties = $deref(requestBodySchema.properties);
        }
        if ('items' in requestBodySchema && requestBodySchema.items) {
            if (Array.isArray(requestBodySchema.items)) {
                properties || (properties = $deref(requestBodySchema.items[0]).properties);
            }
            else {
                properties || (properties = (_d = $deref(requestBodySchema.items).properties) !== null && _d !== void 0 ? _d : {});
            }
        }
        properties || (properties = {});
        for (const propertyName in properties) {
            const property = $deref(properties[propertyName]);
            const key = compact([propertyName, property.description]).join(': ');
            addKeyToPathMapping(key, pathMapping, fullPath);
        }
    }
}
function addOperationObjectToPathMapping(operationObject, pathMapping, fullPath) {
    var _a, _b, _c;
    const key = (_c = (_b = (_a = operationObject.description) !== null && _a !== void 0 ? _a : operationObject.summary) !== null && _b !== void 0 ? _b : operationObject.operationId) !== null && _c !== void 0 ? _c : fullPath;
    addKeyToPathMapping(key, pathMapping, fullPath);
}
function createMetadataMap(trimmedToOriginalKeyMap, pathMapping, api) {
    return Object.fromEntries(Object.entries(trimmedToOriginalKeyMap).map(([trimmedKey, originalKey]) => [
        trimmedKey,
        {
            paths: Array.from(pathMapping.get(originalKey)),
            provider: api.info.title,
            id: originalKey,
        },
    ]));
}
function createMetadataHashMap(metadataMap) {
    return Object.fromEntries(Object.entries(metadataMap).map(([trimmedKey, metadata]) => [
        trimmedKey,
        objecthash(metadata),
    ]));
}
function getKeysToEmbed(trimmedToOriginalKeyMap, storedEmbeddings, metadataHashMap) {
    return Object.keys(trimmedToOriginalKeyMap).filter((key) => {
        if (!key)
            return false;
        const storedEmbedding = storedEmbeddings.find((embedding) => embedding.id === key);
        if (storedEmbedding) {
            const hash = metadataHashMap[key];
            if (storedEmbedding.metadataHash === hash) {
                return false;
            }
        }
        return true;
    });
}
function addKeyToPathMapping(key, pathMapping, fullPath) {
    var _a;
    if (!pathMapping.has(key))
        pathMapping.set(key, new Set());
    (_a = pathMapping.get(key)) === null || _a === void 0 ? void 0 : _a.add(fullPath);
}
